import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, obj = authed
  const [loading, setLoading] = useState(true);
  const lastRefreshRef = useRef(0);

  const refreshMe = useCallback(async (force = false) => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    // 60s throttle — gereksiz /auth/me çağrılarını önle
    const now = Date.now();
    if (!force && now - lastRefreshRef.current < 60000) return null;
    lastRefreshRef.current = now;
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("token");
        setUser(false);
      })
      .finally(() => setLoading(false));
  }, []);

  // Sekme tekrar görünür olunca ya da pencere odak alınca yetkileri tazele.
  // Böylece yönetici ek yetki verdikten sonra kullanıcı F5 yapmadan güncel yetkileri alır.
  useEffect(() => {
    if (!user) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshMe();
    };
    const onFocus = () => refreshMe();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [user, refreshMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", data.token);
    // Login response bazen eski cache'li user verebilir — hemen /auth/me ile tazele
    setUser(data.user);
    refreshMe();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
